import { motion } from 'motion/react'
import { Gift, Tag, Clock, Leaf, Package } from 'lucide-react'
import { OptimizedImage } from '@/shared/ui/optimized-image'
import {
  formatSetPrice,
  getSetComponentsAmount,
  getSetEffectivePrice,
  getSetEstimatedSessions,
  getSetSavings,
  getSetSavingsPercent,
  getSetScenarioLabel,
  getSetTeaCount,
  getSetTeawareCount,
  getSetTotalTeaWeight,
  setComponentLabel,
} from '@/entities/product/lib/set-logic'
import type { ProductSet, ProductSetComponent } from '@/entities/product'

export interface ProductSetDetailsProps {
  set: ProductSet
}

/**
 * Виджет: Детали подарочного набора для карточки товара.
 *
 * Показывает: сценарий, состав (чай/посуда с миниатюрами), экономию комплекта,
 * упаковку и количество чаепитий. Без выбора веса — набор продаётся фикс-ценой.
 */
export function ProductSetDetails({ set }: ProductSetDetailsProps) {
  const price = getSetEffectivePrice(set)
  const componentsAmount = getSetComponentsAmount(set)
  const savings = getSetSavings(set)
  const savingsPercent = getSetSavingsPercent(set)
  const sessions = getSetEstimatedSessions(set)
  const teaCount = getSetTeaCount(set)
  const teawareCount = getSetTeawareCount(set)
  const totalWeight = getSetTotalTeaWeight(set)
  const scenario = getSetScenarioLabel(set)

  return (
    <div className="space-y-5">
      {/* Сценарий */}
      <div className="inline-flex items-center gap-2 rounded-lg bg-primary/10 dark:bg-primary-dark/20 px-3 py-1.5">
        <Gift size={16} className="text-primary dark:text-primary-light" />
        <span className="text-sm font-medium text-primary dark:text-primary-light">{scenario}</span>
      </div>

      {/* Краткие метрики */}
      <div className="grid grid-cols-3 gap-2">
        <Metric icon={<Leaf size={16} />} value={String(teaCount)} label="чаёв" />
        {teawareCount > 0 ? (
          <Metric icon={<Package size={16} />} value={String(teawareCount)} label="посуды" />
        ) : (
          <Metric icon={<Leaf size={16} />} value={totalWeight ? `${totalWeight} г` : '—'} label="чая" />
        )}
        {sessions > 0 && (
          <Metric icon={<Clock size={16} />} value={`≈ ${sessions}`} label="чаепитий" />
        )}
      </div>

      {/* Экономия комплекта */}
      {savings > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 rounded-xl border border-jade/30 bg-jade/10 px-4 py-3"
        >
          <Tag size={18} className="shrink-0 text-jade" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-jade">
              Выгода {formatSetPrice(savings)} ({savingsPercent}%)
            </div>
            <div className="text-xs text-jade/80 mt-0.5">
              Цена набора ниже, чем покупка компонентов по отдельности ({formatSetPrice(componentsAmount)})
            </div>
          </div>
        </motion.div>
      )}

      {/* Состав */}
      <div>
        <h3 className="text-base font-display font-semibold text-ink dark:text-white mb-3">
          Что внутри
        </h3>
        <ul className="space-y-2.5">
          {set.components.map((component: ProductSetComponent) => (
            <li key={component.id} className="flex items-center gap-3">
              <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-paper dark:bg-dark-elevated">
                {component.productImage ? (
                  <OptimizedImage
                    src={component.productImage}
                    alt={component.productName}
                    preset="thumb"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-gray-300">
                    <Package size={18} />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-ink dark:text-white line-clamp-1">
                  {component.productName}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {setComponentLabel(component).split(' · ')[1] || ''}
                </p>
              </div>
              {!component.isAvailable && (
                <span className="text-[11px] font-medium text-gray-400">нет в наличии</span>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Упаковка */}
      {set.packageNote && (
        <div className="rounded-xl bg-paper dark:bg-dark-elevated p-4">
          <h4 className="text-sm font-semibold text-ink dark:text-white mb-1">Упаковка</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{set.packageNote}</p>
        </div>
      )}
    </div>
  )
}

function Metric({ icon, value, label }: { icon: React.ReactNode; value: string; label: string }) {
  return (
    <div className="rounded-xl bg-paper dark:bg-dark-elevated p-3 text-center">
      <div className="flex items-center justify-center text-gray-400 mb-1">{icon}</div>
      <div className="text-base font-bold text-ink dark:text-white">{value}</div>
      <div className="text-[11px] text-gray-500 dark:text-gray-400">{label}</div>
    </div>
  )
}
